import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, Loader2, MessageSquare } from "lucide-react";
import EmailViewerDialog from "@/components/EmailViewerDialog";

/** Lists every email thread tied to a customer. Click opens the full
 *  thread in `EmailViewerDialog` (same component used from the Inbox).
 *
 *  Rendered inside CustomerProfile as the "Email threads" section —
 *  gives the owner a one-click view of every conversation that customer
 *  has with the business, grouped by thread (not by message). */
export default function CustomerEmailTab({ customerId }) {
  const [state, setState] = useState({ loading: true, threads: [] });
  const [openEmailId, setOpenEmailId] = useState(null);

  const load = useCallback(async () => {
    if (!customerId) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/customers/${customerId}/email-threads`);
      setState({ loading: false, threads: data?.threads || [] });
    } catch {
      setState({ loading: false, threads: [] });
    }
  }, [customerId]);
  useEffect(() => { load(); }, [load]);

  const openThread = async (t) => {
    // Need an email_id to open the viewer. Fetch the first message of the
    // thread to get one id. (Could also hit the thread endpoint directly,
    // but the viewer dialog expects an email id.)
    try {
      const { data } = await api.get(`/emails/threads/${t.thread_id}`);
      const first = (data?.messages || [])[0];
      if (first?.id) setOpenEmailId(first.id);
    } catch { /* non-fatal */ }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp" data-testid="customer-email-tab">
      <div className="p-5 border-b border-zinc-100 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500 mb-1">Email conversations</div>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-sky-600"/>
            <span className="font-heading text-lg font-bold">Email threads</span>
            <span className="text-xs text-zinc-500 font-mono-accent">
              · {state.threads.length} {state.threads.length === 1 ? "thread" : "threads"}
            </span>
          </div>
        </div>
      </div>

      <div className="p-3">
        {state.loading && (
          <div className="text-sm text-zinc-500 text-center py-8" data-testid="customer-email-tab-loading">
            <Loader2 className="w-4 h-4 inline mr-2 animate-spin"/> Loading threads…
          </div>
        )}
        {!state.loading && state.threads.length === 0 && (
          <div className="text-center py-10" data-testid="customer-email-tab-empty">
            <Mail className="w-8 h-8 text-zinc-300 mx-auto mb-2"/>
            <div className="text-sm text-zinc-500">
              No email threads for this customer yet.
            </div>
          </div>
        )}
        {!state.loading && state.threads.map((t) => (
          <button
            key={t.thread_id}
            type="button"
            onClick={() => openThread(t)}
            className="w-full text-left flex items-start gap-3 p-3 rounded-md hover:bg-zinc-50 border border-transparent hover:border-zinc-200 transition-colors"
            data-testid={`customer-email-tab-thread-${t.thread_id}`}
          >
            <div className={`mt-1 w-2 h-2 rounded-full ${t.unread > 0 ? "bg-[#FF5A00]" : "bg-zinc-300"}`}/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-medium text-sm text-zinc-900 truncate">
                  {t.subject || "(no subject)"}
                </div>
                {t.unread > 0 && (
                  <span className="px-1.5 py-0.5 rounded bg-[#FF5A00] text-white text-[10px] font-mono-accent uppercase tracking-widest">
                    {t.unread} new
                  </span>
                )}
                <span className="ml-auto text-[11px] text-zinc-500 font-mono-accent shrink-0">
                  {t.last_at ? new Date(t.last_at).toLocaleString() : ""}
                </span>
              </div>
              <div className="text-xs text-zinc-500 truncate mt-0.5">
                {t.last_direction === "inbound" ? "From " : "To "}
                <span className="text-zinc-700">{t.last_from || t.last_to || ""}</span>
                {" · "}
                {t.message_count} {t.message_count === 1 ? "message" : "messages"}
              </div>
              {t.last_body_preview && (
                <div className="text-xs text-zinc-500 truncate mt-1">{t.last_body_preview}</div>
              )}
            </div>
          </button>
        ))}
      </div>

      <EmailViewerDialog
        emailId={openEmailId}
        open={!!openEmailId}
        onClose={() => { setOpenEmailId(null); load(); }}
        onChanged={load}
      />
    </div>
  );
}
